import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { fetchClients, type WorkHoursClientRow } from "../services/workHoursService";
import { inputClass } from "../theme";

// ---------------------------------------------------------------------------
// Selector de cliente de la consola (#978–#980, épica #983). En CC-CONSOLE las
// apps internas muestran TODOS los clientes; este selector acota Horas, Equipo
// o Gastos a uno. Los clientes son los de Horas Trabajadas (`work_hours_clients`).
// Quien no puede listarlos (no administra) no ve el selector: sin opciones que
// elegir, no hay nada que mostrar, y el servidor valida igual.
//
// Qué clientes se listan: los configurados por esta empresa y los que aún no
// tienen configuración. `active` NO decide: solo dice si el reporte semanal por
// correo está encendido, y un cliente configurado con el reporte apagado (Dumax)
// sigue teniendo horas, equipo y gastos que acotar. Los que registra otra
// empresa (`lockedByOtherCompany`) no son de esta cuenta y no se ofrecen.
// ---------------------------------------------------------------------------

/** Valor especial: solo lo propio de la compañía (gastos sin cliente). */
export const NO_CLIENT = "none";

/** ¿El cliente entra al selector? Ver la regla en el encabezado. */
export const isSelectableClient = (c: WorkHoursClientRow): boolean => !c.lockedByOtherCompany && (c.configured === true || c.active !== false);

interface Props {
  value: string;
  onChange: (clientId: string) => void;
  /** Agrega la opción «Sin cliente (propio)» (Gastos). */
  includeNone?: boolean;
  /** Etiqueta de «todos»; por omisión «Todos los clientes». */
  allLabel?: string;
  className?: string;
}

export default function ClientScopeSelect({ value, onChange, includeNone = false, allLabel, className }: Props) {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [clients, setClients] = useState<WorkHoursClientRow[] | null>(null);

  useEffect(() => {
    let alive = true;
    fetchClients()
      .then((rows) => alive && setClients(rows.filter(isSelectableClient)))
      .catch(() => alive && setClients([]));
    return () => {
      alive = false;
    };
  }, []);

  if (!clients || clients.length === 0) return null;
  return (
    <select
      aria-label={t("workHours.clients.filter")}
      data-testid="client-scope-select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={className ?? inputClass(isDarkMode)}
    >
      <option value="">{allLabel ?? t("workHours.clients.allClients")}</option>
      {clients.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
        </option>
      ))}
      {includeNone && <option value={NO_CLIENT}>{t("expenses.client.none")}</option>}
    </select>
  );
}
